import { ref, computed } from 'vue';
import {
  type ContactPerson,
  type AdultTravelerFormValues,
  type ChildTravelerFormValues,
  type Traveler,
  type CheckoutFormData,
} from '@/types/schemas/checkout';
import { useCheckoutStore } from '@/stores/checkout';
import type { ContactFormExposed, TravelerFormExposed } from '@/types/checkout';
import { deriveVariant, type TravelerVariant } from '@/types/schemas/checkout/travelerVariant';

/**
 * Checkout Form Management Composable
 *
 * Manages invoice recipient selection and coordinates form validation
 * across child components (CheckoutContactForm, CheckoutTravelerForm).
 *
 * IMPORTANT: This composable does NOT own useForm() instances anymore.
 * Forms are now managed by individual components via defineExpose().
 * Parent (CheckoutPage) collects refs and passes them to validation methods.
 *
 * Usage:
 * ```typescript
 * const checkoutForm = useCheckoutForm();
 *
 * // In CheckoutPage - collect refs from child components
 * const contactFormRef = ref<ContactFormExposed | null>(null);
 * const travelerFormRefs = ref<Map<number, TravelerFormExposed>>(new Map());
 *
 * async function handleSubmit() {
 *   const success = await checkoutForm.saveToStore(
 *     contactFormRef.value,
 *     travelerFormRefs.value
 *   );
 *   if (success) {
 *     // Proceed to next step
 *   }
 * }
 * ```
 */
export function useCheckoutForm() {
  const checkoutStore = useCheckoutStore();

  // Invoice Recipient
  const invoiceRecipient = ref<'solo' | 'accompany'>('solo');

  // Travelers Computed Properties
  /**
   * Get the index of the first adult passenger.
   * Used to identify which passenger is the contact person when invoiceRecipient === 'solo'.
   */
  const firstAdultIndex = computed(() => {
    if (!checkoutStore.passengers) return -1;
    return checkoutStore.passengers.findIndex((p) => p.isAdult);
  });

  /**
   * Get list of traveler indices that need separate forms based on invoice recipient selection.
   *
   * - If 'solo': Contact person IS traveling as first adult → show forms for OTHER travelers
   * - If 'accompany': Contact person NOT traveling → show forms for ALL travelers
   */
  const travelersToShow = computed(() => {
    if (!checkoutStore.passengers) return [];

    const allIndices = checkoutStore.passengers.map((_, index) => index);

    if (invoiceRecipient.value === 'solo') {
      // Skip the first adult (they are the contact person)
      return allIndices.filter((index) => index !== firstAdultIndex.value);
    }

    // 'accompany' - show all travelers
    return allIndices;
  });

  /**
   * Group the traveler indices that need forms by room, preserving index order within
   * each room and sorting rooms ascending. Drives the room-grouped render on the page.
   */
  const travelersByRoom = computed<Array<{ roomNumber: number; indices: number[] }>>(() => {
    if (!checkoutStore.passengers) return [];

    const groups = new Map<number, number[]>();
    for (const index of travelersToShow.value) {
      const roomNumber = checkoutStore.passengers[index]?.roomNumber ?? 1;
      const bucket = groups.get(roomNumber);
      if (bucket) {
        bucket.push(index);
      } else {
        groups.set(roomNumber, [index]);
      }
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([roomNumber, indices]) => ({ roomNumber, indices }));
  });

  /**
   * Get the display number for a traveler within their variant category.
   * Each variant (adult / under18adult / child / infant) is numbered separately.
   *
   * When 'solo': First adult is Traveler 1 (contact person), subsequent adults are 2, 3, etc.
   * When 'accompany': Adults start at 1, 2, 3, etc.
   * Other variants always start at 1 regardless of invoice recipient.
   *
   * @param index - Passenger index in the passengers array
   * @param variant - The traveler's 4-way variant (counted against same-variant peers)
   * @returns Display number for the traveler (1-based)
   */
  function getTravelerDisplayNumber(index: number, variant: TravelerVariant): number {
    if (!checkoutStore.passengers) return 1;

    let count = 0;
    for (let i = 0; i <= index; i++) {
      const passenger = checkoutStore.passengers[i];
      if (passenger && deriveVariant(passenger) === variant) {
        count++;
      }
    }
    return count;
  }

  // Validation Methods (using refs from parent)
  /**
   * Validate all forms based on invoice recipient selection
   * - Always validates contact form
   * - Validates traveler forms for travelers that need separate forms (travelersToShow)
   *
   * @param contactFormRef - Ref to contact form exposed methods
   * @param travelerFormRefs - Map of traveler index to form exposed methods
   * @returns Promise resolving to true if all forms are valid
   */
  async function validateAllForms(
    contactFormRef: ContactFormExposed | null,
    travelerFormRefs: Map<number, TravelerFormExposed | null>
  ): Promise<boolean> {
    let allValid = true;

    // 1. Validate contact form
    if (!contactFormRef) {
      return false;
    }

    const contactResult = await contactFormRef.validate();
    if (!contactResult.valid) {
      allValid = false;
    }

    // 2. Validate ALL traveler forms (don't stop on first failure)
    for (const id of travelersToShow.value) {
      const formRef = travelerFormRefs.get(id);

      if (!formRef) {
        allValid = false;
        continue;
      }

      const result = await formRef.validate();
      if (!result.valid) {
        allValid = false;
      }
    }

    return allValid;
  }

  // Data Collection
  /**
   * Collect all form data into CheckoutFormData structure
   * Only includes travelers that have separate forms (based on travelersToShow)
   *
   * @param contactFormRef - Ref to contact form exposed methods
   * @param travelerFormRefs - Map of traveler index to form exposed methods
   * @returns Complete checkout form data or null if refs are missing
   */
  function getFormData(
    contactFormRef: ContactFormExposed | null,
    travelerFormRefs: Map<number, TravelerFormExposed | null>
  ): CheckoutFormData | null {
    if (!contactFormRef) {
      console.error('[useCheckoutForm] Contact form ref is null');
      return null;
    }

    const contactData = contactFormRef.getValues() as ContactPerson;

    const travelersData: Traveler[] = [];
    // Only collect data from travelers that have forms (travelersToShow)
    for (const id of travelersToShow.value) {
      const formRef = travelerFormRefs.get(id);
      const passenger = checkoutStore.passengers?.[id];

      if (formRef && passenger) {
        const values = formRef.getValues();
        travelersData.push({
          ...values,
          id,
          type: deriveVariant(passenger),
        } as Traveler);
      }
    }

    return {
      contactPerson: contactData,
      invoiceRecipient: invoiceRecipient.value,
      travelers: travelersData,
    };
  }

  // Store Integration
  /**
   * Validate all forms and save data to checkout store
   *
   * @param contactFormRef - Ref to contact form exposed methods
   * @param travelerFormRefs - Map of traveler index to form exposed methods
   * @returns Promise resolving to true if validation passed and data saved
   */
  async function saveToStore(
    contactFormRef: ContactFormExposed | null,
    travelerFormRefs: Map<number, TravelerFormExposed | null>
  ): Promise<boolean> {
    const isValid = await validateAllForms(contactFormRef, travelerFormRefs);
    if (!isValid) {
      console.error('[useCheckoutForm] Validation failed, data not saved');
      return false;
    }

    const formData = getFormData(contactFormRef, travelerFormRefs);
    if (!formData) {
      console.error('[useCheckoutForm] Failed to collect form data');
      return false;
    }

    checkoutStore.setTravelerData(formData);
    return true;
  }

  // Initial Values Helpers (for child components)
  /**
   * Get initial values for contact person form
   * Restores from store if available (user navigating back)
   */
  function getInitialContactValues(): Partial<ContactPerson> | undefined {
    const existing = checkoutStore.travelerData?.contactPerson;
    if (existing) {
      return existing;
    }
    return undefined;
  }

  /**
   * Get initial values for traveler form by index
   * Restores from store if available (excluding metadata fields id/type)
   */
  function getInitialTravelerValues(
    index: number
  ): Partial<AdultTravelerFormValues | ChildTravelerFormValues> | undefined {
    const existing = checkoutStore.travelerData?.travelers.find((t) => t.id === index);
    if (!existing) return undefined;

    if (existing.type === 'adult') {
      // Return only form-relevant fields (exclude id and type)
      return {
        salutation: existing.salutation,
        firstName: existing.firstName,
        lastName: existing.lastName,
        birthDate: existing.birthDate,
        street: existing.street,
        postalCode: existing.postalCode,
        location: existing.location,
        country: existing.country,
        nationality: existing.nationality,
        mobile: existing.mobile,
        email: existing.email,
      };
    }

    // Gender-style variants (under18adult / child / infant) share the child field set.
    return {
      gender: existing.gender,
      firstName: existing.firstName,
      lastName: existing.lastName,
      birthDate: existing.birthDate,
      nationality: existing.nationality,
    };
  }

  // Form State
  const isSubmitting = ref(false);

  // Public API
  return {
    invoiceRecipient,
    travelersToShow,
    travelersByRoom,
    firstAdultIndex,
    isSubmitting,

    getTravelerDisplayNumber,
    getInitialContactValues,
    getInitialTravelerValues,
    validateAllForms,
    getFormData,
    saveToStore,
  };
}
